import { useState } from 'react';
import { ChevronDown, ChevronRight, BarChart3, Table, FileText, Grid, Filter, Puzzle } from 'lucide-react';
import { COMPONENT_TEMPLATES, ComponentTemplate } from '../../types/components';
import { generateId } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { IconRenderer } from '../../lib/icon-map';
import { getCustomWidgetsForTenant } from '../../custom/index';
import { useAuthStore } from '../../stores/auth.store';
import { useWorkspaceStore } from '../../stores/workspace.store';

interface ComponentPaletteProps {
  onAddComponent: (component: any) => void;
}

export default function ComponentPalette({ onAddComponent }: ComponentPaletteProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);
  const workspaceSettings = useWorkspaceStore((s) => s.currentWorkspace?.settings);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };
  const handleAddComponent = (template: ComponentTemplate) => {
    const newComponent = {
      id: generateId(),
      type: template.type,
      position: {
        x: 0,
        y: 0,
        w: template.defaultSize.w,
        h: template.defaultSize.h,
      },
      config: { ...template.defaultConfig },
    };

    onAddComponent(newComponent);
  };

  const categories = Array.from(
    new Set(COMPONENT_TEMPLATES.map((t) => t.category))
  );

  const customWidgets = isSuperAdmin ? getCustomWidgetsForTenant(workspaceSettings) : [];

  const handleAddCustomWidget = (widget: ReturnType<typeof getCustomWidgetsForTenant>[number]) => {
    onAddComponent({
      id: generateId(),
      type: widget.type,
      position: { x: 0, y: 0, w: widget.defaultSize.w, h: widget.defaultSize.h },
      config: { ...widget.defaultConfig },
    });
  };

  return (
    <Card className="rounded-xl p-6 border-0">
      <h3 className="text-lg font-semibold mb-4">Componentes Disponíveis</h3>

      {categories.map((category) => (
        <div key={category} className="mb-6 last:mb-0">
          <Button
            variant="ghost"
            onClick={() => toggleCategory(category)}
            className="flex items-center gap-2 w-full justify-start text-sm font-medium mb-3 uppercase h-auto p-0 hover:bg-transparent"
          >
            {expandedCategories.has(category) ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <span className="flex items-center gap-2">
              {category === 'analytics' && <><BarChart3 className="w-4 h-4" /> Analytics</>}
              {category === 'data' && <><Table className="w-4 h-4" /> Dados</>}
              {category === 'content' && <><FileText className="w-4 h-4" /> Conteúdo</>}
              {category === 'layout' && <><Grid className="w-4 h-4" /> Layout</>}
              {category === 'filters' && <><Filter className="w-4 h-4" /> Filtros</>}
            </span>
          </Button>

          {expandedCategories.has(category) && (
            <div className="grid grid-cols-1 gap-2">
              {COMPONENT_TEMPLATES.filter((t) => t.category === category).map(
                (template) => (
                  <div
                    key={template.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        "application/x-component-template",
                        template.id
                      );
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                    onClick={() => handleAddComponent(template)}
                    title="Clique para adicionar ou arraste para dentro de um container"
                    className="cursor-grab active:cursor-grabbing"
                  >
                    <Button
                      asChild
                      variant="outline"
                      tabIndex={-1}
                      className="flex items-center gap-3 p-3 h-auto w-full justify-start hover:bg-primary/5 hover:border-primary/50 transition-all pointer-events-none"
                    >
                      <div>
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <IconRenderer icon={template.icon} className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <p className="font-medium text-sm flex items-center gap-2">
                            {template.name}
                            {template.category === 'filters' && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                Header
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground break-words whitespace-normal">{template.description}</p>
                        </div>
                      </div>
                    </Button>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      ))}

      {customWidgets.length > 0 && (
        <div className="mb-6 last:mb-0">
          <Button
            variant="ghost"
            onClick={() => toggleCategory('__custom__')}
            className="flex items-center gap-2 w-full justify-start text-sm font-medium mb-3 uppercase h-auto p-0 hover:bg-transparent"
          >
            {expandedCategories.has('__custom__') ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <span className="flex items-center gap-2">
              <Puzzle className="w-4 h-4" /> Exclusivos
            </span>
          </Button>

          {expandedCategories.has('__custom__') && (
            <div className="grid grid-cols-1 gap-2">
              {customWidgets.map((widget) => (
                <Button
                  key={widget.type}
                  variant="outline"
                  onClick={() => handleAddCustomWidget(widget)}
                  className="flex items-center gap-3 p-3 h-auto justify-start hover:bg-primary/5 hover:border-primary/50 transition-all"
                >
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <IconRenderer icon={widget.icon} className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-medium text-sm">{widget.name}</p>
                    <p className="text-xs text-muted-foreground break-words whitespace-normal">{widget.description}</p>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
